import React, { useState, useEffect, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { fetchAssetMappingsOnSearch, invalidateSearchAssetMappings } from 'actions/content.jsx';
import { Button, Dropdown, Form, Row, Col, InputGroup } from 'react-bootstrap';
import _ from 'lodash';
import cx from 'classnames';
import LinesEllipsis from 'react-lines-ellipsis';
import TruncatedTextWithTooltip from 'components/TruncatedTextWithTooltip';
import StoryboardContext from "components/Storyboard/context";
import { guid } from 'services/util';
import storyboardRoleService from 'services/storyboardRoles.service';

import { conditions } from 'constants/filters';
import { ContentCard } from '../Editor/widgets/content/sectionManager/components/contentCard';
import styles from './assetFilter.module.scss';

export const PropertyContext = React.createContext([]);

export const AssetFilter = ({ index, value, onChange, onInsert, onDelete, canDelete, showAddFilter, inline, filters, filterFor }) => {
  const properties = React.useContext(PropertyContext);
  const isJourneyFilter = filterFor == "journey" ;
  const [filterAll, setFilterAll] = useState(_.get(value, 'operator') === "IS_ALL");
  const values = _.get(value, 'value', []) || [];
  const colStylesCondition = values.length > 0;
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));

  
  const handlePropertyChange = (item) => {
    var filter = {
      ...value,
      property: item,
      operator: 'IS',
      value: null
    };
    onChange(filter);
    setFilterAll(false);
  }

  const handleConditionChange = (operator) => {
    var filter = {
      ...value,
      operator: operator
    };
    onChange(filter);

    if(operator === "IS_ALL") {
      setFilterAll(true);
    } else {
      setFilterAll(false);
    }
  }

  const handleValueChange = (changedValue) => {
    var filter = {
      ...value,
      value: changedValue,
    };
    onChange(filter);
    setFilterAll(false);
  }

  const handleDelete = () => {
    var filter = {
      ...value,
      operator: 'IS'
    };
    onChange(filter);
    setFilterAll(!filterAll);
  }
  
  return (
    <Row className={cx(styles.assetFilterRow, 'mx-0', {[styles.journeyAssetFilterRow]: isJourneyFilter })}>
      <Col className={styles.conditional}>
        <PropertyTypeFilter options={properties} value={value.property} onChange={handlePropertyChange} />
        {!filterAll &&
          <ConditionFilter value={value} onChange={handleConditionChange} filterIndex={index} filters={filters}/>
        }
      </Col>
      <Col className={cx(styles.filterValues, {[styles.journeyFilterValues]: colStylesCondition})}>
        {!filterAll &&
          <ValueComponent value={value} onChange={handleValueChange} inline={inline} filterFor={filterFor}/>
        }
        {filterAll &&
          <FilterAllValueComponent onDelete={handleDelete}/>
        }
      </Col>
      <Col className={cx(styles.actions, {[styles.journeyActions]: (isJourneyFilter && showAddFilter) })}>
        {showAddFilter &&
          <Button className={styles.addButton} onClick={onInsert} variant="primary" size='sm' disabled={filterAll || !storyboardRoleService.canEditStoryboard(storyboard)}>
            <i className={cx(styles.icon, 'cleverstory-plus')} />
            Add Filter
          </Button>
        }
        {canDelete &&
          <Button variant="info" onClick={() => { onDelete(index) }} className={styles.closeButton}>
            <i className={cx(styles.icon, 'cleverstory-close')} />
          </Button>
        }
      </Col>
    </Row>
  )
}

const PropertyTypeFilter = ({ value, disabled, onChange }) => {
  const properties = React.useContext(PropertyContext);

  const property = properties.find(item => {
    if (value.type === 'CUSTOM_FIELD') {
      return value.fieldId === item.value.fieldId;
    }
    return item.value.type === value.type
  });
    
  const handleChange = (data) => {
    const selectedOption = properties.find(p => {
      return p.value === data.value;
    })
    onChange(selectedOption.value);
  }

  return (
    <Dropdown disabled={disabled} className={styles.filterDropdown}>
      <Dropdown.Toggle className={styles.selected}>
        <Button size='sm' variant='primary' className={styles.button}>
          <span className={styles.filterType}>
            <i className={cx(styles.typesIcon, `cleverstory-${_.get(property, 'icon')}`)} />
            <TruncatedTextWithTooltip content={_.get(property, 'label')} maxWidth={230} placement='top'/>
          </span>
          <i className={cx(styles.icon, 'cleverstory-drop-down')} />
        </Button>
      </Dropdown.Toggle>
      <Dropdown.Menu className={styles.typesMenu}>
        {properties.map((data, index) => {
          return (
            <Dropdown.Item onClick={() => { handleChange(data) }} key={index}>
              <i className={cx(styles.filterTypeIcon, `cleverstory-${data.icon}`)} />
              <TruncatedTextWithTooltip content={data.label} maxWidth={230} placement='top' />
            </Dropdown.Item>
          )
          })
        }
      </Dropdown.Menu>
    </Dropdown>
  )
}

const ConditionFilter = ({ value, disabled, onChange, filterIndex, filters }) => {
  const properties = React.useContext(PropertyContext);
  const property = properties.find(item => item.value.type === value.property.type);
  const condition = conditions.find(item => item.type === property.data_type) || {};
  const operator = (condition.options || []).find(item => item.value === value.operator);
  const canFilterAll = filters.length <= 1 && filterIndex == 0; 

  const handleChange = (data) => {
    const value = data.value;
    onChange(value);
  }

  return (
    <Dropdown disabled={disabled} className={styles.filterDropdown}>
      <Dropdown.Toggle className={styles.selected}>
        <Button size='sm' variant='primary' className={styles.button}>
          <span className={styles.filterType}>
            {operator.label}
          </span>
          <i className={cx(styles.icon, 'cleverstory-drop-down')} />
        </Button>
      </Dropdown.Toggle>
      <Dropdown.Menu className={styles.typesMenu}>
        {
          (condition.options || []).map((data, index) => {
            if(!canFilterAll && data.value == "IS_ALL") {
                return;
            } 
            return (
              <Dropdown.Item onClick={() => { handleChange(data) }} key={index}>
                {data.label}
              </Dropdown.Item>
            )
          })
        }
      </Dropdown.Menu>
    </Dropdown>
  )
}

/**
 * Value Input
*/
function ValueComponent({ value, disabled, onChange, inline, filterFor }) {
  const properties = React.useContext(PropertyContext);

  const property = properties.find(item => {
    if (value.property.type === 'CUSTOM_FIELD') {
      return value.property.fieldId === item.value.fieldId;
    }
    return item.value.type === value.property.type;
  });

  var type = _.get(property, 'data_type', null);
  if (type === 'asset') {
    return (
      <AssetValue
        property={property}
        value={value}
        onChange={onChange}
        disabled={disabled}
        inline={inline}
        filterFor={filterFor}
      />
    )
  } else if (type === 'list') {
    return (
      <ListValue property={property}
        value={value}
        onChange={onChange}
        disabled={disabled}
        filterFor={filterFor}
      />
    );
  }
  return (
    <InputValue property={property}
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

function AssetValue({ value, disabled, onChange, inline, filterFor }) {
  const dispatch = useDispatch();

  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused]=useState(false);
  const values = _.get(value, 'value', []) || [];
  const isJourneyFilter = filterFor == "journey" ;
  const journeyAssetTypes = ["INTERACTIVE_CONTENT" , "LANDING_PAGE", "RESOURCE_CENTER"];
  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardId = useSelector(state => _.get(state, 'storyboards.storyboard.item.id', _.get(storyboardContext, 'id')));
  const assets = useSelector(state => _.get(state, 'content.assetFilters.items', []));
  const assetsLoading = useSelector(state => _.get(state, 'content.assetFilters.loading', false));

  const filteredAssets = isJourneyFilter 
  ? assets.filter(option => !journeyAssetTypes.includes(option?.asset?.storyboardType))
  : assets;

  useEffect(() => {
    if (query) {
      var request = {
        query: query,
        storyboard: storyboardId,
        excludeStoryboardAssets: inline
      }
      dispatch(fetchAssetMappingsOnSearch(request, 0, 20));
    }
    else {
      dispatch(invalidateSearchAssetMappings());
    }
  }, [query, storyboardId])

  const handleInputChange = (e) => {
    setIsFocused(true)
    setQuery(e.target.value);
  }

  const handleSelect = (id) => {
    var exist = values.some(item => item.id === id);
    if (exist) {
        return;
    }
    var data = assets.find(item => item.id === id);
    var request = [...values, data];
    onChange(request);
  };

  const handleDelete = (data) => {
    var request = (values || []).filter(item => item.id !== data.id);
    onChange(request);
  }

  const handleKeyDown = (event, data) => {
    if(event.key === 'Enter') {
      handleSelect(data);
    }
  };

  const getFieldSelector = (mapping, key) => {
    const fields = _.get(mapping, 'fields') || [];
    const field = fields.find(item => item.name === key);
    return field;
  }

  const getContentForFieldSelector = (mapping, key) => {
    const field = getFieldSelector(mapping, key);
    if (!field) {
      return null;
    }

    return _.get(field, `content`);
  }

  return (
    <>
      {values.map((item, index) => {
        return (
          <div key={`${item}-${index}`} className={cx(styles.filterTags)}>
            <TruncatedTextWithTooltip content={_.get(item, 'asset.name', '')} className={styles.name} maxWidth={230} placement='top' />
            <i className={cx(styles.closeIcon, `cleverstory-close`)} onClick={() => handleDelete(item)} />
          </div>
        );
      })}
      <Dropdown className={styles.assetDropdown} disabled={disabled} show={isFocused} onSelect={handleSelect}>
        <Dropdown.Toggle as={Form.Group} id="dropdown-search" className={styles.assetDropdownToggle}>
            <Form.Label><i className={cx(styles.icon, 'cleverstory-search')} /></Form.Label>
            <InputGroup>
            <Form.Control type="text"
              value={query}
              onChange={handleInputChange}
              onFocus={() => setIsFocused(true)}  // Trigger when input gains focus
              onBlur={() => setIsFocused(false)}
              placeholder="Search" />
              <InputGroup.Text className={cx(styles.loaderContainer, {[styles.showLoader]: (assetsLoading && isFocused) })} id="basic-addon2">
              <i className={cx(styles.loadIcon, 'cleverstory-spinloader')} />
              </InputGroup.Text>
            </InputGroup>
        </Dropdown.Toggle>
        {filteredAssets?.length > 0 ?
            <Dropdown.Menu className={cx(styles.assetsMenu, { [styles.assetsMenuJourney]: isJourneyFilter })}>
              {filteredAssets?.map((result, index) => {
                var iconData = getContentForFieldSelector(result, 'icon');
                var iconUrl = _.get(iconData, 'url', null) || _.get(result, 'asset.icon.key', null);
                return (
                  <Dropdown.Item key={index} onMouseDown={() => handleSelect(result.id)}  onKeyDown={(e) => handleKeyDown(e, result.id)} eventKey={result.id}>
                    <ContentCard selected={false} assetData={result} key={index} className={styles.assetDropdownCard}/>
                  </Dropdown.Item>
                )
              })}
            </Dropdown.Menu>
          : 
          (!assetsLoading && query && isFocused && 
            <Dropdown.Menu className={cx(styles.assetsMenuEmptyWrap)}>
              <Dropdown.Item disabled className={styles.assetsMenuEmpty}>
                <div className={styles.assetsMenuEmptyText}>
                  <i className={cx(styles.icon, 'cleverstory-file-type')} />
                  <p>No Assets Found</p>
                </div>
                <p className={styles.assetsMenuEmptyMessage}>{`We couldn't find anything for "${query}".`}</p>
              </Dropdown.Item>
            </Dropdown.Menu>
          )
        }
      </Dropdown>
    </>
  );
}


function ListValue({ property, value, disabled, onChange, filterFor }) {
  const journeyAssetTypes = ["INTERACTIVE_CONTENT" , "LANDING_PAGE", "RESOURCE_CENTER"];
  const isJourneyFilter = filterFor == "journey" ;
  var options = _.get(property, 'config.options', []);
  const values = _.get(value, 'value', []) || [];

  const filteredOptions = isJourneyFilter 
  ? options.filter(option => !journeyAssetTypes.includes(option.value))
  : options;
  
  const handleSelect = (option) => {
    var request = [...values];
    request = [...request, option];
    request = _.uniq(request);
    onChange(request);
  }

  const handleKeyDown = (event, data) => {
    if (event.key === 'Enter') {
      handleSelect(data);
    }
  };

  const handleDelete = (option) => {
    var request = [...values].filter(item => item !== option);
    onChange(request);
  }

  return (
    <>
      {values.map((item, index) => {
        var option = options.find(o => {
          return o.value === item;
        })
        if(!option) {
          return false;
        }
        return (
          <div key={`${item}-${index}`} className={cx(styles.filterTags)}>
            {option.icon &&
                <i className={cx(styles.filterTypeIcon, `cleverstory-${option.icon}`)} />
            }
            <TruncatedTextWithTooltip content={option.label} className={styles.name} maxWidth={230} placement='top' />
            <i className={cx(styles.closeIcon, `cleverstory-close`)} onClick={() => handleDelete(item)} />
          </div>
        );
      })}

      <Dropdown className={styles.filesDropdown} onSelect={handleSelect} disabled={disabled}>
        <Dropdown.Toggle variant="success" className={styles.selected} id="dropdown-basic">
          <Button size='sm' variant='primary' className={styles.button}>
           Select
          </Button>
        </Dropdown.Toggle>
        <Dropdown.Menu className={styles.typesMenu}>
          {filteredOptions.map((data, index) => {
            return (
              <Dropdown.Item key={index} eventKey={data.value} onKeyDown={(e) => handleKeyDown(e, data.value)}>
                <i className={cx(styles.filterTypeIcon, `cleverstory-${data.icon}`)} />
                <TruncatedTextWithTooltip content={data.label} maxWidth={250} placement='top'/>
              </Dropdown.Item>
            )
          })}
        </Dropdown.Menu>
      </Dropdown>
    </>
  )
}

function InputValue({ value, onChange }) {
  const values = _.get(value, 'value', []) || [];
  const [query, setQuery] = useState('');
  const handleChange = (e) => {
      setQuery(e.target.value);
  }

  const handleSelect = () => {
    if(!query || query.trim().length === 0) {
      return;
    }

    var request = [...values];
    request = [...request, query.toLowerCase()];
    request = _.uniq(request);
    onChange(request);
    setQuery('');
  }

  const handleKeyPressed = (e) => {
    if(e.key === "Enter") {
      handleSelect();
    }
  }

  const handleDelete = (option) => {
    var request = [...values].filter(item => item !== option);
    onChange(request);
  }
    
  return (
    <Fragment>
      {values.map((item, index) => {
        return (
          <div key={`${item}-${index}`} className={cx(styles.filterTags)}>
            <TruncatedTextWithTooltip content={item} className={styles.name} maxWidth={200} placement='top'/>
            <i className={cx(styles.closeIcon, `cleverstory-close`)} onClick={() => handleDelete(item)} />
          </div>
        );
      })}

      <Form.Control
        className={styles.tagsInputFiels}
        type="text"
        placeholder="Type Something"
        value={query}
        onChange={handleChange}
        onBlur={handleSelect}
        onKeyPress={handleKeyPressed}
      />
    </Fragment>
  );
}

function FilterAllValueComponent({onDelete}) {
  return (
    <div className={cx(styles.filterTags)}>
      <TruncatedTextWithTooltip content='Is All' className={styles.name} maxWidth={230} placement='top' />
      <i className={cx(styles.closeIcon, `cleverstory-close`)} onClick={onDelete} />
    </div>
  );
}